import { CarType } from ".";

export const CAR_TYPE_COST_VALUE = {
  [CarType.ICE]: 1,
  [CarType.HYBRID]: 0.5,
  [CarType.EV]: 0.25,
};

export const VAT_RATE = 0.19;

const COMMUTE_DISTANCE_DEFAULT_PRICE_LIMIT = 20;
// Rates are in cents
const DEFAULT_COMMUTE_PRICE_RATE = 30;
const COMMUTE_PRICE_RATE_OVER_DEFAULTS_LIMIT = 35;
const COMMUTE_PRICE_RATE_OVER_DEFAULTS_LIMIT_AFTER_2023 = 38;

const LAST_YEAR_WITHOUT_RATE_CHANGES = 2020;

/**
 * Returns the commute price rate for distances exceeding the default limit based on declaration year.
 * - For tax years 2021-2023: 35 cents per km
 * - For tax years 2024-2026: 38 cents per km
 * - For tax years before 2021 or from 2027 onwards: 30 cents per km
 * @param declarationYear The year of tax declaration
 * @returns The applicable rate in cents per kilometer
 */
export const getCommutePriceRateOverDefaultsLimit = (
  declarationYear: number
) => {
  if (declarationYear >= 2021 && declarationYear <= 2023) {
    return COMMUTE_PRICE_RATE_OVER_DEFAULTS_LIMIT;
  } else if (declarationYear >= 2024 && declarationYear <= 2026) {
    return COMMUTE_PRICE_RATE_OVER_DEFAULTS_LIMIT_AFTER_2023;
  } else if (declarationYear >= 2027) {
    return DEFAULT_COMMUTE_PRICE_RATE;
  } else {
    // For years before 2021, fallback to the original rate
    return DEFAULT_COMMUTE_PRICE_RATE;
  }
};

/**
 * Floors (rounds) the value to the nearest hundreds.
 * (E.g amount = 144505 [EUR 1445.05] results in 140000 [EUR 1400])
 */
export const roundDownCentsToFullHundreds = (amountInCents: number): number => {
  // Convert price to Euros.
  const amountInEuros = amountInCents / 100;

  // Floor the price divided by 100 and then multiply it again by 100 to get a
  // value rounded to closest smallest hundreds.
  const roundedAmountInEuros = Math.floor(amountInEuros / 100) * 100;

  // Return the result converted back to cents
  return roundedAmountInEuros * 100;
};

/**
 * Reduces the gross price based on the car type and floors (rounds) the value to the nearest hundreds.
 */
export const getReducedGrossPrice = ({
  grossPriceInCents,
  carType,
}: {
  grossPriceInCents: number;
  carType: CarType;
}): number => {
  // Reduce the price based on the car type
  const reducedPriceInCents = grossPriceInCents * CAR_TYPE_COST_VALUE[carType];

  return roundDownCentsToFullHundreds(reducedPriceInCents);
};

/**
 * Returns the minimum deductible travel expenses based on the commute days and distance.
 * If distance is minor or equal to a established limit all the calculation is based on
 * one fixed rate. If the distance exceeds that limit, the exceeding distance is calculated
 * with a slightly higher rate.
 */
export const getMinDeductibleTravelExpenses = ({
  commuteDays,
  commuteDistance,
  declarationYear,
}: {
  commuteDays: number;
  commuteDistance: number;
  declarationYear: number;
}): number => {
  if (commuteDays === 0 || commuteDistance === 0) {
    return 0;
  }

  if (
    declarationYear <= LAST_YEAR_WITHOUT_RATE_CHANGES ||
    commuteDistance <= COMMUTE_DISTANCE_DEFAULT_PRICE_LIMIT
  ) {
    return commuteDays * commuteDistance * DEFAULT_COMMUTE_PRICE_RATE;
  }

  const distanceOverLimit =
    commuteDistance - COMMUTE_DISTANCE_DEFAULT_PRICE_LIMIT;

  const amountUnderDistanceLimit =
    commuteDays *
    COMMUTE_DISTANCE_DEFAULT_PRICE_LIMIT *
    DEFAULT_COMMUTE_PRICE_RATE;
  const amountOverDistanceLimit =
    commuteDays *
    distanceOverLimit *
    getCommutePriceRateOverDefaultsLimit(declarationYear);

  return Math.round(amountUnderDistanceLimit + amountOverDistanceLimit);
};
